/** @jsxImportSource react */
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchActasWithComparisons,
  calculateMedian,
  calculateAverage,
} from "../lib/api";
import type { MesaWithComparison } from "../lib/types";
import {
  resolveDistritoId,
  getDistritoName,
} from "../../shared/lib/district-mappings";
import { DispersionChart } from "./DispersionChart";
import { BarrasChart } from "./BarrasChart";
import { ActasPorProvinciaChart } from "./ActasPorProvinciaChart";
import {
  ComparisonTable,
  mesaAggregateComparison,
  mesaComparison,
} from "./ComparisonTable";

export function ActasView() {
  const [selectedProvince, setSelectedProvince] = useState<string>("all");
  const [statMode, setStatMode] = useState<"median" | "average">("median");
  const [chartType, setChartType] = useState<
    "dispersion" | "barras" | "actasPorProvincia" | "comparacion"
  >("comparacion");
  const [barrasMode, setBarrasMode] = useState<"absoluto" | "relativo">(
    "relativo",
  );
  const [selectedMesa, setSelectedMesa] = useState<MesaWithComparison | null>(
    null,
  );

  // Fetch actas with comparisons
  const {
    data: mesasFromAPI = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["actas-with-comparisons"],
    queryFn: fetchActasWithComparisons,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 30, // Refetch automático cada 2 minutos (sin recargar la página)
  });

  // Agregar datos mock a los datos reales si está activado
  const mesas = mesasFromAPI;

  // Mantener mesa seleccionada sincronizada con los datos y filtros
  useEffect(() => {
    if (!selectedMesa) return;

    const updated = mesas.find((mesa) => mesa.id === selectedMesa.id);

    if (!updated) {
      setSelectedMesa(null);
      return;
    }

    if (selectedProvince !== "all") {
      const provinceId = resolveDistritoId(updated.data.distrito);
      if (!provinceId || provinceId !== selectedProvince) {
        setSelectedMesa(null);
        return;
      }
    }

    if (updated !== selectedMesa) {
      setSelectedMesa(updated);
    }
  }, [mesas, selectedMesa, selectedProvince]);
  // Get unique provinces for filtering (normalized to IDs)
  const provinces = useMemo(() => {
    const uniqueProvinceIds = new Set<string>();
    mesas.forEach((mesa) => {
      if (mesa.data.distrito) {
        const provinceId = resolveDistritoId(mesa.data.distrito);
        if (provinceId) {
          uniqueProvinceIds.add(provinceId);
        }
      }
    });
    // Sort by ID and return as array of objects with id and name
    return Array.from(uniqueProvinceIds)
      .sort()
      .map((id) => ({ id, name: getDistritoName(id) || id }));
  }, [mesas]);

  // All mesas with party comparison (para el gráfico - siempre todas)
  const allMesasWithComparison = useMemo(() => {
    return mesas;
  }, [mesas]);

  // Filter mesas by selected province
  const filteredMesas = useMemo(() => {
    let filtered = mesas;

    if (selectedProvince !== "all") {
      filtered = filtered.filter((mesa) => {
        const mesaProvinceId = resolveDistritoId(mesa.data.distrito);
        return mesaProvinceId === selectedProvince;
      });
    }

    return [...filtered].sort((a, b) => {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [mesas, selectedProvince]);

  // Set de IDs filtrados para el gráfico (incluye filtro de provincia Y estado enabled/disabled)
  const filteredIds = useMemo(() => {
    if (selectedMesa) {
      return new Set([String(selectedMesa.id)]);
    }

    return new Set(filteredMesas.map((mesa) => String(mesa.id)));
  }, [filteredMesas, selectedMesa]);

  // Mesas activas para gráficos/tablas (aplica selección o disables)
  const activeMesasForCharts = useMemo(() => {
    if (selectedMesa) {
      return [selectedMesa];
    }

    return filteredMesas;
  }, [filteredMesas, selectedMesa]);

  const dispersionData = useMemo(() => {
    if (selectedMesa) {
      return [selectedMesa];
    }
    return allMesasWithComparison;
  }, [selectedMesa, allMesasWithComparison]);

  const barrasChartData = useMemo(() => {
    if (selectedMesa) {
      return [selectedMesa];
    }
    return filteredMesas;
  }, [selectedMesa, filteredMesas]);

  const actasPorProvinciaData = useMemo(() => {
    // Siempre mostrar TODAS las mesas para este gráfico
    // No aplicar filtros de provincia ni mesa seleccionada
    return mesas;
  }, [mesas]);

  // Calculate statistics
  const { diffLLA, diffFP } = useMemo(() => {
    const comparison = mesaAggregateComparison(activeMesasForCharts, statMode);
    if (!comparison.lla.diff || !comparison.fp.diff) {
      return {
        diffLLA: null,
        diffFP: null,
      };
    }
    return {
      diffLLA: comparison.lla.diff,
      diffFP: comparison.fp.diff,
    };
  }, [activeMesasForCharts, statMode]);

  // Calculate additional metrics
  const totalActas = activeMesasForCharts.length;
  const totalProvinces = provinces.length;
  const totalActasWithPartyData = mesas.length;

  // Countdown to 21:00 (9 PM)
  const [timeUntil21, setTimeUntil21] = useState("");

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const target = new Date();
      target.setHours(21, 0, 0, 0);

      // If it's already past 21:00 today, target tomorrow at 21:00
      if (now > target) {
        target.setDate(target.getDate() + 1);
      }

      const diff = target.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeUntil21(`${hours}h ${minutes}m`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500"></div>
          <p className="mt-4 text-slate-600">Cargando actas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center text-red-600">
          <p>Error al cargar las actas</p>
          <p className="text-sm text-slate-500">{String(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col"
      style={{ backgroundColor: "#F4F8FB" }}
    >
      {/* Main content area - grows to fill space */}
      <div className="flex min-h-0 flex-1 gap-4 p-4">
        {/* Left side: Chart with statistics header or Detail view (via Outlet) */}
        <div className="flex w-2/3 flex-col rounded-lg border border-slate-200 bg-white">
          {/* Chart header */}
          <div
            className="relative shrink-0 border-b border-slate-200 px-3 py-3"
            style={{ backgroundColor: "#E5EDF3" }}
          >
            <div className="flex items-center justify-center">
              {/* Toggle buttons - principais (centrados) */}
              <div className="flex gap-1">
                <button
                  onClick={() => setChartType("comparacion")}
                  className={`w-32 px-4 py-2 text-sm font-semibold rounded border shadow-sm transition ${
                    chartType === "comparacion"
                      ? "border-[#777] bg-[#777] text-white"
                      : "border-slate-200 bg-white/80 text-slate-700"
                  }`}
                >
                  Comparación
                </button>
                {/* <button
                  onClick={() => setChartType("dispersion")}
                  className={`w-32 px-4 py-2 text-sm font-semibold rounded border shadow-sm transition ${
                    chartType === "dispersion"
                      ? "border-[#777] bg-[#777] text-white"
                      : "border-slate-200 bg-white/80 text-slate-700"
                  }`}
                >
                  Dispersión
                </button>
                <button
                  onClick={() => setChartType("barras")}
                  className={`w-32 px-4 py-2 text-sm font-semibold rounded border shadow-sm transition ${
                    chartType === "barras"
                      ? "border-[#777] bg-[#777] text-white"
                      : "border-slate-200 bg-white/80 text-slate-700"
                  }`}
                >
                  Barras
                </button> */}
                <button
                  onClick={() => setChartType("actasPorProvincia")}
                  className={`w-32 px-4 py-2 text-sm font-semibold rounded border shadow-sm transition ${
                    chartType === "actasPorProvincia"
                      ? "border-[#777] bg-[#777] text-white"
                      : "border-slate-200 bg-white/80 text-slate-700"
                  }`}
                >
                  Actas/Prov
                </button>
              </div>
            </div>

            {/* Toggle absoluto/relativo solo en modo barras (derecha absoluta) */}
            {chartType === "barras" && (
              <div className="absolute right-3 top-1/2 flex -translate-y-1/2 gap-1">
                <button
                  onClick={() => setBarrasMode("absoluto")}
                  className={`px-3 py-1.5 text-xs font-medium rounded border shadow-sm transition ${
                    barrasMode === "absoluto"
                      ? "bg-slate-400 text-white border-slate-400"
                      : "bg-white/80 text-slate-600 border-slate-200"
                  }`}
                >
                  Absoluto
                </button>
                <button
                  onClick={() => setBarrasMode("relativo")}
                  className={`px-3 py-1.5 text-xs font-medium rounded border shadow-sm transition ${
                    barrasMode === "relativo"
                      ? "bg-slate-400 text-white border-slate-400"
                      : "bg-white/80 text-slate-600 border-slate-200"
                  }`}
                >
                  Relativo
                </button>
              </div>
            )}
          </div>

          {/* Chart area */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch justify-stretch">
            {chartType === "dispersion" ? (
              diffLLA !== null && diffFP !== null ? (
                <DispersionChart
                  data={dispersionData}
                  filteredIds={filteredIds}
                  llaAverage={diffLLA}
                  fpAverage={diffFP}
                />
              ) : null
            ) : chartType === "barras" ? (
              <BarrasChart
                data={barrasChartData}
                statMode={statMode}
                barrasMode={barrasMode}
              />
            ) : chartType === "actasPorProvincia" ? (
              <ActasPorProvinciaChart data={actasPorProvinciaData} />
            ) : (
              <ComparisonTable
                mesas={activeMesasForCharts}
                selectedMesa={selectedMesa}
                statMode={statMode}
              />
            )}
          </div>
        </div>

        {/* Right side: Mesa list */}
        <div
          className={`flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white w-1/3`}
        >
          {/* Actas header - always visible but disabled when mesa is selected */}
          <div
            className={`shrink-0 border-b border-slate-200 px-3 py-3 transition ${selectedMesa ? "opacity-50 pointer-events-none" : ""}`}
            style={{ backgroundColor: "#E5EDF3" }}
          >
            <div className="flex items-center gap-3">
              {/* Party statistics badges */}
              <div className="flex gap-1">
                {diffLLA !== null && diffFP !== null ? (
                  <>
                    <PartyDiffBadge
                      value={diffLLA}
                      label="LLA"
                      colorScheme="purple"
                      isHeader={true}
                    />
                    <PartyDiffBadge
                      value={diffFP}
                      label="FP"
                      colorScheme="blue"
                      isHeader={true}
                    />
                  </>
                ) : null}
              </div>

              {/* Toggle switch */}
              <button
                onClick={() =>
                  setStatMode(statMode === "median" ? "average" : "median")
                }
                disabled={!!selectedMesa}
                className={`flex items-center gap-2 rounded border px-2 py-1 text-xs font-medium shadow-sm transition ${
                  selectedMesa
                    ? "border-slate-200 bg-white/50 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 bg-white/80 text-slate-700"
                }`}
                title={`Cambiar a ${statMode === "median" ? "promedio" : "mediana"}`}
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12M8 12h12m-12 5h12M3 7h.01M3 12h.01M3 17h.01"
                  />
                </svg>
                <span>{statMode === "median" ? "Mediana" : "Promedio"}</span>
              </button>

              {/* Spacer to push right content */}
              <div className="flex-1"></div>

              {/* Metrics */}
              <div className="flex gap-2 text-xs">
                <div className="rounded bg-white px-2 py-1 border border-slate-200">
                  <span className="font-semibold text-slate-900">
                    {totalActas}
                  </span>
                  {totalActas !== totalActasWithPartyData && (
                    <span className="text-slate-600">
                      {" "}
                      de {totalActasWithPartyData}
                    </span>
                  )}
                  <span className="text-slate-600"> actas</span>
                </div>
                <div className="rounded bg-white px-2 py-1 border border-slate-200">
                  <span className="font-semibold text-slate-900">
                    {totalProvinces}/24
                  </span>
                  <span className="text-slate-600"> prov</span>
                </div>
              </div>

              {/* Filters */}
              <select
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                disabled={!!selectedMesa}
                className={`rounded border px-2 py-1 text-xs font-medium shadow-sm transition ${
                  selectedMesa
                    ? "border-slate-200 bg-white/50 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 bg-white/80 text-slate-700"
                }`}
              >
                <option value="all">Todas</option>
                {provinces.map((province) => (
                  <option key={province.id} value={province.id}>
                    {province.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Selected mesa header - shown alongside the mesa list header */}
          {selectedMesa && (
            <div
              className="shrink-0 border-b border-blue-500 bg-white px-3 transition border-l-4 shadow-sm"
              style={{
                height: "90px",
                paddingTop: "5px",
                paddingBottom: "5px",
              }}
            >
              <div className="flex items-center gap-3 h-full">
                {/* Eye icon - back button */}
                <button
                  onClick={() => setSelectedMesa(null)}
                  className="shrink-0 text-blue-500 hover:text-blue-600 transition"
                  title="Volver"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Party badges for selected mesa */}
                <div className="flex gap-1">
                  <PartyDiffBadgesForMesa mesa={selectedMesa} />
                </div>

                {/* Mesa info */}
                <div className="flex-1">
                  <p className="font-bold text-slate-900">
                    {selectedMesa.data.distrito || "Sin distrito"} -{" "}
                    {selectedMesa.data.circuito || "Sin circuito"}
                  </p>
                  <p className="text-sm text-slate-600">
                    Mesa {selectedMesa.data.mesaNumber || "???"}
                  </p>
                </div>

                {/* Close/VER button to match MesaItem style */}
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => setSelectedMesa(null)}
                    className="flex items-center gap-1.5 rounded border border-blue-300 bg-blue-100 text-blue-700 shadow-sm hover:bg-blue-200 px-3 py-2 transition"
                    title="Cerrar"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="text-xs font-medium">CERRAR</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Image section when mesa is selected - expands to fill space */}
          {selectedMesa && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="h-full flex items-center justify-center">
                {selectedMesa.image_id ? (
                  <img
                    src={`https://actas.urbana.visualizando.ar/images/${selectedMesa.image_id}`}
                    alt="Acta electoral seleccionada"
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex items-center justify-center text-sm text-slate-500">
                    Imagen no disponible
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actas list - only shown when no mesa is selected */}
          {!selectedMesa && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="flex flex-col">
                {/* Mesa items */}
                {filteredMesas.map((mesa: MesaWithComparison) => (
                  <MesaItem
                    key={mesa.id}
                    mesa={mesa}
                    enabled={true}
                    isSelected={false}
                    onSelect={() => setSelectedMesa(mesa)}
                  />
                ))}

                {filteredMesas.length === 0 && (
                  <div className="py-8 text-center text-slate-400">
                    No hay actas para mostrar
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual mesa item component
interface MesaItemProps {
  mesa: MesaWithComparison;
  enabled: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

function MesaItem({ mesa, enabled, isSelected, onSelect }: MesaItemProps) {
  const baseClasses =
    "flex items-center gap-3 border-b border-slate-200 px-3 transition border-l-4";
  const backgroundClasses = enabled
    ? isSelected
      ? "bg-blue-50 hover:bg-blue-100"
      : "bg-white hover:bg-slate-50"
    : "bg-slate-100";
  const borderLeftClasses = isSelected
    ? "border-blue-500 shadow-sm"
    : "border-transparent";
  const disabledClasses = enabled ? "" : "opacity-60";
  const toggleButtonClasses = enabled
    ? isSelected
      ? "text-blue-500 hover:text-blue-600"
      : "text-slate-400 hover:text-slate-600"
    : "text-slate-400 hover:text-slate-500";

  return (
    <div
      className={`${baseClasses} ${backgroundClasses} ${borderLeftClasses} ${disabledClasses}`}
      style={{ height: "90px", paddingTop: "5px", paddingBottom: "5px" }}
    >
      {/* Percentage difference badges */}
      <div className="flex gap-1">
        <PartyDiffBadgesForMesa mesa={mesa} />
      </div>

      {/* Mesa info */}
      <div className="flex-1">
        <p className="font-bold text-slate-900">
          {mesa.data.distrito || "Sin distrito"} -{" "}
          {mesa.data.circuito || "Sin circuito"}
        </p>
        <p className="text-sm text-slate-600">
          Mesa {mesa.data.mesaNumber || "???"}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex shrink-0 gap-2">
        {/* View image button */}
        <button
          onClick={onSelect}
          className={`flex items-center gap-1.5 rounded border px-3 py-2 transition ${
            isSelected
              ? "border-blue-300 bg-blue-100 text-blue-700 shadow-sm hover:bg-blue-200"
              : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
          }`}
          title="Ver acta"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="text-xs font-medium">VER</span>
        </button>
      </div>
    </div>
  );
}

function PartyDiffBadgesForMesa({ mesa }: { mesa: MesaWithComparison }) {
  const comparison = mesaComparison(mesa);
  const lla = comparison.lla;
  const fp = comparison.fp;
  if (!lla.pct2023 || !lla.pct2025 || !fp.pct2023 || !fp.pct2025) {
    return null;
  }
  return (
    <div className="flex gap-1">
      <PartyDiffBadge
        value={lla.pct2025 - lla.pct2023}
        colorScheme="purple"
        label="LLA"
      />
      <PartyDiffBadge
        value={fp.pct2025 - fp.pct2023}
        colorScheme="blue"
        label="FP"
      />
    </div>
  );
}

// Party-specific difference badge with label
interface PartyDiffBadgeProps {
  value: number;
  label: string;
  colorScheme: "blue" | "purple";
  isHeader?: boolean;
}

function PartyDiffBadge({
  value,
  label,
  colorScheme,
  isHeader = false,
}: PartyDiffBadgeProps) {
  const isPositive = value >= 0;
  const formatted = Math.abs(value).toFixed(1);

  const bgColor = colorScheme === "blue" ? "bg-blue-50" : "bg-purple-50";
  const textColor =
    colorScheme === "blue" ? "text-blue-700" : "text-purple-700";

  // Header: borde grueso y color fuerte
  // Actas: borde fino y color muy suave
  const borderStyle = isHeader
    ? `border-2 ${colorScheme === "blue" ? "border-blue-500" : "border-purple-500"}`
    : `border ${colorScheme === "blue" ? "border-blue-200" : "border-purple-200"}`;

  const fontWeight = isHeader ? "font-bold" : "font-medium";

  return (
    <div
      className={`flex w-16 flex-col items-center justify-center gap-0.5 rounded ${borderStyle} ${bgColor} px-3 py-2`}
    >
      {/* Up/Down arrow */}
      <svg
        className={`h-4 w-4 ${textColor}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={isPositive ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
        />
      </svg>
      {/* Value */}
      <span className={`text-lg ${fontWeight} ${textColor} leading-none`}>
        {isPositive ? "+" : "-"}
        {formatted}
      </span>
      {/* Party label */}
      <span
        className={`text-xs font-semibold uppercase ${textColor} opacity-70 leading-none`}
      >
        {label}
      </span>
    </div>
  );
}
